import { z } from "zod";

/**
 * Document layout — a company-wide setting for how generated document bodies
 * look (text size, spacing, heading/table/highlight/callout styles) and how much
 * formatting the AI uses. Edited in company settings, snapshotted onto each
 * document at generation (`output.layout`) so a document keeps its look after
 * the setting changes. The letterhead still owns the header, footer and brand
 * accent. Defaults reproduce the look documents had before the setting existed.
 *
 * Mirrors `api/src/modules/documents/document-layout.ts` — keep the shapes and
 * the PDF tokens in sync.
 */

export const FORMATTING_LEVELS = ["minimal", "balanced", "rich"] as const;
export const BODY_SIZES = ["sm", "md", "lg"] as const;
export const SPACINGS = ["compact", "normal", "relaxed"] as const;
export const BODY_ALIGNS = ["left", "justify"] as const;
export const HEADING_STYLES = ["plain", "accent", "underline", "caps"] as const;
export const TABLE_STYLES = ["bordered", "striped", "minimal"] as const;
export const TABLE_HEADERS = ["neutral", "accent"] as const;
export const HIGHLIGHT_COLORS = ["yellow", "green", "blue", "pink"] as const;
export const CALLOUT_STYLES = ["soft", "outline"] as const;

/**
 * A complete layout, every key required — the form schema. (The API declares
 * the same keys with defaults; here defaults are applied by
 * {@link normalizeDocumentLayout} so form input and output types match.)
 */
export const documentLayoutSchema = z.object({
  formatting: z.enum(FORMATTING_LEVELS),
  useHeadings: z.boolean(),
  useTables: z.boolean(),
  useHighlights: z.boolean(),
  useCallouts: z.boolean(),
  fontSize: z.enum(BODY_SIZES),
  lineSpacing: z.enum(SPACINGS),
  paragraphSpacing: z.enum(SPACINGS),
  textAlign: z.enum(BODY_ALIGNS),
  headingStyle: z.enum(HEADING_STYLES),
  tableStyle: z.enum(TABLE_STYLES),
  tableHeader: z.enum(TABLE_HEADERS),
  highlightColor: z.enum(HIGHLIGHT_COLORS),
  calloutStyle: z.enum(CALLOUT_STYLES),
});

export type DocumentLayout = z.infer<typeof documentLayoutSchema>;

export const DEFAULT_DOCUMENT_LAYOUT: DocumentLayout = {
  formatting: "balanced",
  useHeadings: true,
  useTables: true,
  useHighlights: true,
  useCallouts: true,
  fontSize: "md",
  lineSpacing: "normal",
  paragraphSpacing: "normal",
  textAlign: "left",
  headingStyle: "plain",
  tableStyle: "bordered",
  tableHeader: "neutral",
  highlightColor: "yellow",
  calloutStyle: "soft",
};

/**
 * Any stored value → a complete layout: missing keys take their defaults, and
 * an invalid value falls back to the default layout.
 */
export function normalizeDocumentLayout(raw: unknown): DocumentLayout {
  const parsed = documentLayoutSchema.partial().safeParse(raw ?? {});
  return parsed.success ? { ...DEFAULT_DOCUMENT_LAYOUT, ...parsed.data } : DEFAULT_DOCUMENT_LAYOUT;
}

// ── PDF tokens (mirror the API) ─────────────────────────────────────────────

const BODY_FONT_SIZE = { sm: 10, md: 11, lg: 12 } as const;
const LINE_HEIGHT = { compact: 1.35, normal: 1.5, relaxed: 1.7 } as const;
const PARAGRAPH_GAP = { compact: 7, normal: 12, relaxed: 16 } as const;

export const HIGHLIGHT_HEX = {
  yellow: "#fde68a",
  green: "#bbf7d0",
  blue: "#bfdbfe",
  pink: "#fbcfe8",
} as const;

/** `hex` mixed toward white — `amount` 0 is white, 1 is the colour itself. */
export function tint(hex: string, amount: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const mix = (c: number) => Math.round(255 - (255 - c) * amount);
  const r = mix((n >> 16) & 255);
  const g = mix((n >> 8) & 255);
  const b = mix(n & 255);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

export type PdfLayoutTokens = {
  layout: DocumentLayout;
  accent: string;
  fontSize: number;
  lineHeight: number;
  paragraphGap: number;
  highlight: string;
};

export function pdfLayoutTokens(raw: unknown, accent: string): PdfLayoutTokens {
  const layout = normalizeDocumentLayout(raw);
  return {
    layout,
    accent,
    fontSize: BODY_FONT_SIZE[layout.fontSize],
    lineHeight: LINE_HEIGHT[layout.lineSpacing],
    paragraphGap: PARAGRAPH_GAP[layout.paragraphSpacing],
    highlight: HIGHLIGHT_HEX[layout.highlightColor],
  };
}

// ── On-screen rendering ─────────────────────────────────────────────────────

/**
 * Props for the element wrapping a document body in the editor, e-sign page and
 * settings preview. The `data-doc-*` attributes select the styles in
 * `ui/styles.css`; `--doc-accent` feeds the accent heading/table variants.
 */
export function documentLayoutProps(
  raw: unknown,
  accent: string,
): { style: React.CSSProperties } & Record<`data-doc-${string}`, string> {
  const l = normalizeDocumentLayout(raw);
  return {
    "data-doc-layout": "",
    "data-doc-size": l.fontSize,
    "data-doc-line": l.lineSpacing,
    "data-doc-gap": l.paragraphSpacing,
    "data-doc-align": l.textAlign,
    "data-doc-heading": l.headingStyle,
    "data-doc-table": l.tableStyle,
    "data-doc-table-header": l.tableHeader,
    "data-doc-highlight": l.highlightColor,
    "data-doc-callout": l.calloutStyle,
    style: { "--doc-accent": accent } as React.CSSProperties,
  };
}

// ── Settings labels ─────────────────────────────────────────────────────────

export const LAYOUT_LABELS = {
  formatting: {
    minimal: "Minimal",
    balanced: "Balanced",
    rich: "Rich",
  },
  fontSize: { sm: "Small", md: "Medium", lg: "Large" },
  spacing: { compact: "Compact", normal: "Normal", relaxed: "Relaxed" },
  textAlign: { left: "Left", justify: "Justified" },
  headingStyle: {
    plain: "Plain",
    accent: "Brand colour",
    underline: "Underlined",
    caps: "Small caps",
  },
  tableStyle: { bordered: "Bordered", striped: "Striped", minimal: "Minimal" },
  tableHeader: { neutral: "Grey", accent: "Brand colour" },
  highlightColor: { yellow: "Yellow", green: "Green", blue: "Blue", pink: "Pink" },
  calloutStyle: { soft: "Filled", outline: "Outlined" },
} as const;
